import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { Icon, type IconName } from './icon';
import { statusToneClasses, type StatusToneLike } from '../../lib/statusTone';

function alertToneVariant(tone: StatusToneLike) {
  const tokens = statusToneClasses(tone);
  const iconClass = tokens.icon.replace(/^text-/, '[&>svg]:text-');
  return cn(tokens.surfaceBackground, tokens.surfaceBorder, tokens.text, iconClass);
}

const alertVariants = cva(
  'relative w-full rounded-sm border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-text-primary',
  {
    variants: {
      variant: {
        default: 'bg-surface text-text-primary',
        destructive: alertToneVariant('danger'),
        success: alertToneVariant('success'),
        warning: alertToneVariant('warning'),
        info: alertToneVariant('info'),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const iconMap: Record<string, IconName> = {
  default: 'info',
  destructive: 'error',
  success: 'check_circle',
  warning: 'warning',
  info: 'info',
};

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  showIcon?: boolean;
}

export function Alert({
  className,
  variant = 'default',
  showIcon = true,
  children,
  ...props
}: AlertProps) {
  const iconName = iconMap[variant || 'default'];

  return (
    <div role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      {showIcon && <Icon name={iconName} className="text-base" />}
      {children}
    </div>
  );
}

type AlertTitleProps = React.HTMLAttributes<HTMLHeadingElement>;

export function AlertTitle({ className, children, ...props }: AlertTitleProps) {
  return (
    <h5 className={cn('mb-1 font-medium leading-none tracking-tight', className)} {...props}>
      {children}
    </h5>
  );
}

type AlertDescriptionProps = React.HTMLAttributes<HTMLParagraphElement>;

export function AlertDescription({ className, ...props }: AlertDescriptionProps) {
  return <div className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />;
}
